/**
 * artifact-stream-parser
 *
 * Caller contract: pure function, called repeatedly on accumulating streamed
 * text. Each call receives the full accumulated text so far (not just the
 * latest chunk).
 *
 * Partial-tag suppression: if the tail of the text (after the last complete or
 * in-progress artifact block) ends with an unterminated `<artifact …` opening
 * tag, that suffix is stripped from displayText so raw markup never flashes to
 * the user mid-stream. Only the LAST `<artifact`-prefixed suffix is checked;
 * earlier occurrences of the literal string `<artifact` that appear in prose
 * (e.g. `<artifact-exempel>` or `<artifactory>`) are left unchanged.
 *
 * Known limitation (MVP): a `</artifact>` that appears literally inside
 * artifact content will be treated as the closing tag of the surrounding block,
 * truncating the content at that point. Nested artifact tags are not supported.
 */
import type {
  ApplyUpdatesResult,
  ArtifactCommand,
  ArtifactType,
  ExtractResult,
  ParsedArtifactBlock,
  UpdatePair,
} from "../artifact-types"

import { findOpenFence, splitFenceLines } from "@/lib/fence-tracking"

// Quote-aware attribute pattern so values containing ">" do not end the match early.
const OPEN_TAG = /<artifact\s+((?:[^"'>]|"[^"]*"|'[^']*')*)>/g
// Separate instance (no capture group) used only for depth-aware close matching,
// so its lastIndex bookkeeping can't clash with the OPEN_TAG matchAll iterator.
const NESTED_OPEN = /<artifact\s+(?:[^"'>]|"[^"]*"|'[^']*')*>/g
const CLOSE_TAG = "</artifact>"

/**
 * Find the `</artifact>` that matches the artifact opened at `fromIndex`,
 * skipping any nested `<artifact>…</artifact>` pairs. A bare `</artifact>` with
 * no preceding nested open is still the match (preserves the "embedded close
 * truncates" behaviour); a genuinely nested inner artifact no longer truncates
 * the outer block at the inner's close, nor leaves the outer's own `</artifact>`
 * stranded as literal text. Returns -1 while the matching close hasn't streamed
 * in yet.
 */
function findMatchingCloseTag(text: string, fromIndex: number): number {
  let depth = 0
  let i = fromIndex
  while (i < text.length) {
    const close = text.indexOf(CLOSE_TAG, i)
    if (close === -1) return -1
    NESTED_OPEN.lastIndex = i
    const open = NESTED_OPEN.exec(text)
    if (open && open.index < close) {
      // A nested artifact opens before this close — descend into it.
      depth++
      i = open.index + open[0].length
      continue
    }
    if (depth === 0) return close
    depth--
    i = close + CLOSE_TAG.length
  }
  return -1
}

const VALID_TYPES: ArtifactType[] = [
  "markdown",
  "html",
  "react",
  "svg",
  "mermaid",
  "chart",
  "sheet",
  "slides",
]
const VALID_COMMANDS: ArtifactCommand[] = ["create", "update", "rewrite"]

function parseAttributes(attrText: string): Record<string, string> {
  const attrs: Record<string, string> = {}
  // Accept both double- and single-quoted values. OPEN_TAG already tolerates
  // single quotes when finding the tag, so a value that needs them (e.g.
  // title='He said "hi"') must not be silently dropped here.
  for (const match of attrText.matchAll(/(\w+)=(?:"([^"]*)"|'([^']*)')/g)) {
    // Exactly one of the double-/single-quoted groups matches; the other is
    // undefined at runtime (TS types them as string, hence `||` not `??`).
    attrs[match[1]] = match[2] || match[3] || ""
  }
  return attrs
}

/**
 * Removes a trailing partial `</artifact>` closing tag (any prefix of it,
 * e.g. "</a", "</artifact") from the end of mid-stream artifact content.
 */
function stripTrailingPartialCloseTag(content: string): string {
  const lastOpenAngle = content.lastIndexOf("<")
  if (lastOpenAngle === -1) return content
  const suffix = content.slice(lastOpenAngle)
  if (suffix.length < CLOSE_TAG.length && CLOSE_TAG.startsWith(suffix)) {
    return content.slice(0, lastOpenAngle).replace(/\n+$/, "")
  }
  return content
}

/**
 * `keyScope` seeds the fallback identifier for an artifact tag that omits
 * `identifier`. It must be unique per message when the result is merged across
 * messages (rebuild() passes the messageId): otherwise two different messages
 * that both omit `identifier` would both derive `artifact-0` and get folded into
 * the same store entry, splicing unrelated version histories together. Defaults
 * to "" for single-message/display callers where cross-message collision can't
 * happen.
 */
/**
 * True when `index` sits inside an open fenced code block in the display text
 * (prose only, artifacts already stripped). A literal `<artifact …>` inside a
 * code sample (e.g. the assistant explaining the feature or quoting its own
 * prompt) must render as code, not be parsed as a real artifact — otherwise
 * the visible sample is replaced by a spurious link and a bogus entry lands
 * in the store. Delegates to the shared `findOpenFence` (CommonMark-correct:
 * full fence-run length, matching marker char, no info string on close)
 * rather than reimplementing fence tracking a third time in this codebase.
 */
function isInsideCodeFence(displayText: string): boolean {
  return findOpenFence(splitFenceLines(displayText).map(({ content }) => content)) !== null
}

export function extractArtifacts(text: string, keyScope = ""): ExtractResult {
  const blocks: ParsedArtifactBlock[] = []
  let displayText = ""
  let cursor = 0
  const fallbackPrefix = keyScope ? `artifact-${keyScope}-` : "artifact-"

  for (const match of text.matchAll(OPEN_TAG)) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (match.index === undefined) continue
    const openStart = match.index
    const openEnd = openStart + match[0].length
    if (openStart < cursor) continue
    // Leave <artifact> tags inside a fenced code block as literal text.
    // Check fence parity in displayText (prose only, artifacts stripped) — counting
    // fences in the raw message includes those inside earlier artifact bodies and
    // causes false positives when an artifact contains code blocks.
    const proseBeforeTag = displayText + text.slice(cursor, openStart)
    if (isInsideCodeFence(proseBeforeTag)) continue

    const attrs = parseAttributes(match[1])
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const identifier = attrs.identifier ?? `${fallbackPrefix}${blocks.length}`
    const type = VALID_TYPES.includes(attrs.type as ArtifactType)
      ? (attrs.type as ArtifactType)
      : "markdown"
    const command = VALID_COMMANDS.includes(attrs.command as ArtifactCommand)
      ? (attrs.command as ArtifactCommand)
      : "create"
    const title = attrs.title || identifier

    const closeIndex = findMatchingCloseTag(text, openEnd)
    const complete = closeIndex !== -1
    const contentEnd = complete ? closeIndex : text.length
    const blockEnd = complete ? closeIndex + CLOSE_TAG.length : text.length

    let rawContent = text.slice(openEnd, contentEnd).replace(/^\n+/, "").replace(/\n+$/, "")
    if (!complete) {
      // Mid-stream the closing tag may be half-received ("…</artifa"). Hide any
      // trailing prefix of "</artifact>" so it never flashes in the live canvas.
      rawContent = stripTrailingPartialCloseTag(rawContent)
    }

    blocks.push({
      identifier,
      type,
      title,
      command,
      rawContent,
      complete,
    })

    // Sanitize title for display link label by removing square brackets
    const sanitizedTitle = title.replace(/[[\]]/g, "")
    displayText += text.slice(cursor, openStart) + `[${sanitizedTitle}](artifact:${identifier})`
    cursor = blockEnd
  }

  let tail = text.slice(cursor)
  // Suppress only a trailing unterminated <artifact …> at the very end of the
  // tail. Use lastIndexOf so earlier prose occurrences of "<artifact" (e.g.
  // "<artifact-exempel>") are not consumed by the pattern.
  const lastArtifactIdx = tail.lastIndexOf("<artifact")
  if (lastArtifactIdx !== -1) {
    const suffix = tail.slice(lastArtifactIdx)
    if (/^<artifact(\s[^>]*)?$/.test(suffix)) {
      tail = tail.slice(0, lastArtifactIdx)
    }
  }
  displayText += tail

  return { displayText, blocks }
}

export const ARTIFACT_HREF_PREFIX = "artifact:"

/**
 * Extracts the artifact identifier from an `artifact:` href rendered by
 * ReactMarkdown. The markdown pipeline percent-encodes link URLs (e.g.
 * `artifact:snygg-j%C3%A4mf%C3%B6relsetabell`), so the identifier must be
 * decoded before it can be used as a store key. Returns null for non-artifact
 * hrefs; falls back to the raw slice if the encoding is malformed.
 */
export function identifierFromArtifactHref(href: string | undefined): string | null {
  if (!href?.startsWith(ARTIFACT_HREF_PREFIX)) return null
  const raw = href.slice(ARTIFACT_HREF_PREFIX.length)
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

export function parseUpdateCommands(rawContent: string): UpdatePair[] {
  const pairs: UpdatePair[] = []
  const regex = /<old_str>([\s\S]*?)<\/old_str>\s*<new_str>([\s\S]*?)<\/new_str>/g
  for (const match of rawContent.matchAll(regex)) {
    pairs.push({ oldStr: match[1], newStr: match[2] })
  }
  return pairs
}

export function applyUpdates(baseContent: string, updates: UpdatePair[]): ApplyUpdatesResult {
  let content = baseContent
  for (const [index, { oldStr, newStr }] of updates.entries()) {
    if (oldStr === "") return { ok: false, error: "no_match", failedIndex: index }
    const first = content.indexOf(oldStr)
    if (first === -1) return { ok: false, error: "no_match", failedIndex: index }
    if (content.indexOf(oldStr, first + 1) !== -1) {
      return { ok: false, error: "multiple_matches", failedIndex: index }
    }
    content = content.slice(0, first) + newStr + content.slice(first + oldStr.length)
  }
  return { ok: true, content }
}
